import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateWaiver } from "@factory/query/waiver.query";
import { WaiverFormRule, WaiverFormSchema } from "@factory/form/form.rule";
import { waiverFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewWaiver() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapCreateWaiver } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();
  const { getUserRole } = useUserIdentityContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Waiver is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addWaiver, isLoading: isCreating } =
    useQyCreateWaiver(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<WaiverFormSchema>({
    defaultValues: waiverFormDefault,
    resolver: zodResolver(WaiverFormRule),
  });
  const { handleSubmit, watch } = formMethod;
  const description = watch("description");
  const subtitle = watch("subtitle");
  const name = watch("name");
  const fontSize = watch("fontSize");

  const handleValidate = (form: WaiverFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateWaiver(updatedForm);
    addWaiver(formData);
  };
  const handleValidateError = (err: FieldErrors<WaiverFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    description,
    subtitle,
    name,
    fontSize,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
